import { filterSquadKvRows } from './squadKvRows';

/**
 * SQUAD Fondsdaten — dynamic Axion fields + EUR formatting.
 * Only dynamic values are formatted; a value typed by hand is shown exactly as typed.
 * - Kurs: `x.xxx,xx EUR`
 * - Anteilsklassenvolumen / Fondsvolumen: `x.xxx.xxx EUR` (whole euros, rounded)
 */

export type SquadDynamicCell = {
  isDynamic: true;
  fields: Array<{
    type: 'table' | 'text';
    table?: string;
    field?: string;
    valueType?: 'number' | 'string' | 'date';
    text?: string;
  }>;
};

export type SquadFondsdatenValue = string | SquadDynamicCell;

/** Fondsdaten fields that support isDynamic: the WKN plus the money fields. */
export const SQUAD_DYNAMIC_FONDSDATEN_KEYS = [
  'wkn',
  'kursNav',
  'anteilsklassenvolumen',
  'fondsvolumen',
] as const;

export type SquadDynamicFondsdatenKey = (typeof SQUAD_DYNAMIC_FONDSDATEN_KEYS)[number];

/** Fetched without the factsheet date — see fetchSquadFondsdatenDynamicValues. */
export const SQUAD_TIME_INVARIANT_FONDSDATEN_KEYS = ['wkn'] as const;

export function isSquadDynamicFondsdatenKey(key: string): key is SquadDynamicFondsdatenKey {
  return (SQUAD_DYNAMIC_FONDSDATEN_KEYS as readonly string[]).includes(key);
}

export function isSquadDynamicCell(value: unknown): value is SquadDynamicCell {
  return Boolean(
    value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      (value as SquadDynamicCell).isDynamic === true &&
      Array.isArray((value as SquadDynamicCell).fields),
  );
}

/** NAV — SQUAD stores unit price as `anteilwert`. Suffix → `217,58 EUR`. */
export function makeSquadKursNavDynamic(): SquadDynamicCell {
  return {
    isDynamic: true,
    fields: [
      {
        type: 'table',
        table: 'cixon_stammdaten_extension',
        field: 'anteilwert',
        valueType: 'number',
      },
      { type: 'text', text: ' EUR' },
    ],
  };
}

const makeStammdatenNumberDynamic = (field: string): SquadDynamicCell => ({
  isDynamic: true,
  fields: [
    {
      type: 'table',
      table: 'cixon_stammdaten_extension',
      field,
      valueType: 'number',
    },
  ],
});

/**
 * Fondsvolumen — `fondsvermoegen` is the whole fund: every share class of a fund carries
 * the same figure. Text suffix omitted; the display layer adds ` EUR` after fetch.
 */
export function makeSquadFondsvolumenDynamic(): SquadDynamicCell {
  return makeStammdatenNumberDynamic('fondsvermoegen');
}

/**
 * WKN — `stammdaten_extension.wkn`, fetched as text: a WKN can look like a number
 * (Green Balance `564968`), and valueType `number` would format it.
 */
export function makeSquadWknDynamic(): SquadDynamicCell {
  return {
    isDynamic: true,
    fields: [
      {
        type: 'table',
        table: 'cixon_stammdaten_extension',
        field: 'wkn',
        valueType: 'string',
      },
    ],
  };
}

/**
 * The fixed WKN every SQUAD document carried until 09.10.2026 (mapper default). It is
 * Value A's old WKN — the delivery and the exchanges say A0B7ZX — and it was printed for
 * every share class. Only this exact value is replaced; a WKN typed by hand stays.
 */
export const SQUAD_LEGACY_DEFAULT_WKN = 'A0B7TK';

/**
 * Anteilsklassenvolumen — `askvermoegen` is the share class alone; the classes of a fund
 * sum to its `fondsvermoegen` (checked for fondnr 105110, 105130, 103550 on 24.09.2026).
 */
export function makeSquadAnteilsklassenvolumenDynamic(): SquadDynamicCell {
  return makeStammdatenNumberDynamic('askvermoegen');
}

/**
 * The default both volume rows shipped with until 25.09.2026 — share class included, which
 * printed the fund's volume as the share class's. Matches that default's exact shape and
 * nothing else, so a CellConfig with further fields, its own ISIN or a fallback value
 * stays untouched.
 */
function isLegacyFondsvermoegenDefault(cell: SquadDynamicCell): boolean {
  if (cell.fields.length !== 1) return false;
  const [f] = cell.fields as Array<Record<string, unknown>>;
  const keys = Object.keys(f).sort().join(',');
  return (
    keys === 'field,table,type,valueType' &&
    f.type === 'table' &&
    f.table === 'cixon_stammdaten_extension' &&
    f.field === 'fondsvermoegen' &&
    f.valueType === 'number'
  );
}

export function parseGermanNumber(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  let s = String(raw).trim();
  // No digit, no number: `Number('')` is 0, and a missing NAV used to print as "0,00 EUR".
  if (!/\d/.test(s)) return null;
  s = s
    .replace(/\s/g, '')
    .replace(/EUR/gi, '')
    .replace(/Mio\.?/gi, '')
    .replace(/€/g, '');
  // DE: 40.900.000,00 or 40,90
  if (/\.\d{3}/.test(s) || (s.includes('.') && s.includes(','))) {
    s = s.replace(/\./g, '').replace(',', '.');
  } else if (s.includes(',')) {
    s = s.replace(',', '.');
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** `EUR 100,00` / `100,00 EUR` / number → `100,00 EUR`. */
export function formatSquadEur(raw: unknown): string {
  if (raw == null || raw === '') return '';
  if (isSquadDynamicCell(raw)) return '';
  const asString = String(raw).trim();
  if (!asString) return '';

  // Already suffix form with optional Mio.
  if (/Mio\.?\s*EUR\s*$/i.test(asString) && !/^EUR\s/i.test(asString)) {
    return asString.replace(/\s+/g, ' ').replace(/Mio\.?\s*EUR/i, 'Mio. EUR').trim();
  }
  if (/\d.*\s*EUR\s*$/i.test(asString) && !/^EUR\s/i.test(asString)) {
    return asString.replace(/\s+/g, ' ').replace(/\s*EUR\s*$/i, ' EUR').trim();
  }

  // Prefix form: EUR 100,00 | EUR 40,90 Mio.
  const prefix = asString.match(/^EUR\s+(.+)$/i);
  if (prefix) {
    const rest = prefix[1].trim();
    if (/Mio\.?\s*$/i.test(rest)) {
      return `${rest.replace(/\s*Mio\.?\s*$/i, '').trim()} Mio. EUR`;
    }
    return `${rest} EUR`;
  }

  const n = parseGermanNumber(asString);
  if (n == null) return asString;
  return `${n.toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} EUR`;
}

/**
 * Dynamic volume → `400.000.000 EUR` (DE thousands, whole euros, rounded).
 * Used for both Anteilsklassenvolumen (`askvermoegen`) and Fondsvolumen (`fondsvermoegen`).
 */
export function formatSquadAbsoluteEur(raw: unknown): string {
  if (raw == null || raw === '') return '';
  if (isSquadDynamicCell(raw)) return '';
  const asString = String(raw).trim();
  if (!asString) return '';

  // Reject Mio. forms — those belong on Fondsvolumen
  if (/Mio/i.test(asString)) {
    return formatSquadMioEur(asString);
  }

  const n = parseGermanNumber(asString);
  if (n == null) {
    // Already looks like `400.000.000 EUR` without a decimal comma — keep normalized
    if (/^\d[\d.]*\s*EUR$/i.test(asString) && !asString.includes(',')) {
      return asString.replace(/\s+/g, ' ').replace(/\s*EUR\s*$/i, ' EUR').trim();
    }
    return asString;
  }

  const whole = Math.round(n);
  return `${whole.toLocaleString('de-DE', {
    maximumFractionDigits: 0,
  })} EUR`;
}

/** Absolute amount or DE-formatted string → `40,90 Mio. EUR`. */
export function formatSquadMioEur(raw: unknown): string {
  if (raw == null || raw === '') return '';
  if (isSquadDynamicCell(raw)) return '';
  const asString = String(raw).trim();
  if (!asString) return '';

  // Already Mio. EUR (any order)
  if (/Mio/i.test(asString) && /EUR/i.test(asString)) {
    return formatSquadEur(asString);
  }
  if (/Mio\.?\s*$/i.test(asString) && !/EUR/i.test(asString)) {
    return `${asString.replace(/\s*Mio\.?\s*$/i, '').trim()} Mio. EUR`;
  }

  const n = parseGermanNumber(asString);
  if (n == null) return formatSquadEur(asString);

  // Values already in Mio. range (< 10_000) keep as-is; otherwise divide by 1e6
  const mio = Math.abs(n) >= 10_000 ? n / 1_000_000 : n;
  return `${mio.toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} Mio. EUR`;
}

/**
 * True when a fetched dynamic value carries nothing from the data. The server answers a
 * missing field with an empty string, so only the cell's text parts come back (" EUR" for
 * the NAV). A configured default value ("n/a") is something the editor chose and counts.
 */
export function isEmptySquadDynamicValue(raw: unknown, cell?: SquadDynamicCell): boolean {
  const squash = (s: unknown) => String(s ?? '').replace(/\s+/g, '');
  const text = squash(raw);
  if (!text) return true;
  if (!cell) return false;
  const textParts = cell.fields
    .filter((f) => f.type === 'text')
    .map((f) => f.text || '')
    .join('');
  return text === squash(textParts);
}

/** Formats a fetched dynamic value; static values are shown verbatim and never pass through here. */
export function formatSquadFondsdatenDisplay(key: string, raw: unknown): string {
  if (raw == null || raw === '') return '';
  if (isSquadDynamicCell(raw)) return '';
  if (key === 'anteilsklassenvolumen' || key === 'fondsvolumen') {
    return formatSquadAbsoluteEur(raw);
  }
  if (key === 'kursNav' || key === 'erstausgabepreis') {
    return formatSquadEur(raw);
  }
  return String(raw);
}

/** Resolve KV rows for preview/PDF — dynamic cells use cache values when present. Empty values are omitted. */
export function resolveSquadFondsdatenRows(
  map: Record<string, unknown> | undefined,
  keys: Array<{ key: string; label: string }>,
  cache?: Map<string, string>,
): Array<{ label: string; value: string }> {
  return filterSquadKvRows(
    keys.map(({ key, label }) => {
      const raw = map?.[key];
      if (isSquadDynamicCell(raw)) {
        const cached = cache?.get(key);
        if (!isEmptySquadDynamicValue(cached, raw)) {
          return { label, value: formatSquadFondsdatenDisplay(key, cached) };
        }
        return { label, value: '' };
      }
      return { label, value: String(raw ?? '') };
    }),
  );
}

/** Default dynamic config for a dynamic Fondsdaten key — every key needs its own case. */
export function defaultDynamicConfigForKey(key: SquadDynamicFondsdatenKey): SquadDynamicCell {
  switch (key) {
    case 'wkn':
      return makeSquadWknDynamic();
    case 'kursNav':
      return makeSquadKursNavDynamic();
    case 'anteilsklassenvolumen':
      return makeSquadAnteilsklassenvolumenDynamic();
    case 'fondsvolumen':
      return makeSquadFondsvolumenDynamic();
    default: {
      // A key added to SQUAD_DYNAMIC_FONDSDATEN_KEYS without a case here fails to compile
      // instead of silently printing the fund volume.
      const unhandled: never = key;
      throw new Error(`No dynamic default for Fondsdaten key ${String(unhandled)}`);
    }
  }
}

/**
 * Money fields on load and on save.
 * - Missing key → dynamic default. An empty string is not missing: it is what the Dynamic
 *   switch writes when an editor turns a row manual, so it stays manual (and empty rows hide).
 * - Static values are kept exactly as typed — the editor owns the currency text.
 * - Anteilsklassenvolumen still on the old `fondsvermoegen` default → `askvermoegen`.
 * - WKN still on the old fixed default `A0B7TK` → dynamic (see SQUAD_LEGACY_DEFAULT_WKN).
 * - Other dynamic configs kept as-is; NAV gets its EUR suffix if missing.
 */
export function ensureSquadDynamicFondsdaten(
  fondsdaten: Record<string, unknown> | undefined | null,
): Record<string, SquadFondsdatenValue> {
  const next: Record<string, SquadFondsdatenValue> = {
    ...((fondsdaten || {}) as Record<string, SquadFondsdatenValue>),
  };

  for (const key of SQUAD_DYNAMIC_FONDSDATEN_KEYS) {
    const current = next[key];
    if (isSquadDynamicCell(current)) {
      if (key === 'kursNav') {
        const hasEurText = (current.fields || []).some(
          (f) => f.type === 'text' && /EUR/i.test(String(f.text || '')),
        );
        if (!hasEurText && (current.fields || []).some((f) => f.type === 'table')) {
          next[key] = {
            isDynamic: true,
            fields: [...current.fields, { type: 'text', text: ' EUR' }],
          };
        }
      }
      if (key === 'anteilsklassenvolumen' && isLegacyFondsvermoegenDefault(current)) {
        next[key] = makeSquadAnteilsklassenvolumenDynamic();
      }
      continue;
    }

    if (current == null) {
      next[key] = defaultDynamicConfigForKey(key);
    } else if (key === 'wkn' && String(current).trim() === SQUAD_LEGACY_DEFAULT_WKN) {
      next[key] = makeSquadWknDynamic();
    }
  }

  return next;
}
